import { PostDetailsType } from '@anytag/shared-types';
import { useQueryHelper, useSelectItem } from '@anytag/shared-hooks';
import { getYoutubePostUrl, getYoutubeVideoUrl } from '@anytag/shared-utils';
import { useYoutubeAnalyticsPostQuery, useInfluencerYoutubeAnalyticsPostQuery } from '@anytag/graphql';
import { PostModal, usePostModalContext } from '../shared';
import { YoutubeAnalyticsRelatedPostsModal } from '../youtube-analytics-related-posts-modal';

export type TemplatePostDetailsType = Pick<PostDetailsType, 'id' | 'mainSocialAccountId'>;

export interface TemplateProps {
  className?: string;
  isAnalytics?: boolean;
}

export const Template = ({ className, isAnalytics }: TemplateProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { selectedItem, setSelectedItem, resetSelectedItem } = useSelectItem<string>();
  const { closeModal, selectedPost } = usePostModalContext<TemplatePostDetailsType>();
  const { data, loading } = useInfluencerYoutubeAnalyticsPostQuery({
    skip: !selectedPost?.id || !selectedPost?.mainSocialAccountId || isAnalytics,
    variables: {
      socialAccountId: Number(selectedPost?.mainSocialAccountId),
      youtubeAnalyticsPostId: Number(selectedPost?.id)
    },
    onError: (error) => {
      closeModal?.();
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  });
  const { data: analyticsData, loading: fetchingAnalyticsData } = useYoutubeAnalyticsPostQuery({
    skip: !selectedPost?.id || !selectedPost?.mainSocialAccountId || !isAnalytics,
    variables: {
      youtubeAnalyticsAccountId: Number(selectedPost?.mainSocialAccountId),
      youtubeAnalyticsPostId: Number(selectedPost?.id)
    },
    onError: (error) => {
      closeModal?.();
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  });

  const details = data?.influencerYoutubeAnalyticsPost || analyticsData?.youtubeAnalyticsPost;

  return (
    <>
      <PostModal.DetailsTemplate
        className={className}
        loading={loading || fetchingAnalyticsData}
        mediaPreviewProps={{ onTagClick: setSelectedItem }}
        data={
          details
            ? {
                socialMedia: 'YOUTUBE',
                postDate: details?.postDate,
                caption: details?.videoCaption,
                postUrl: getYoutubePostUrl(details?.videoId),
                video: {
                  tags: details?.videoTags,
                  title: details?.videoTitle,
                  url: getYoutubeVideoUrl(details?.videoId)
                },
                comments: details.videoComments.map((comment) => ({
                  content: comment.content,
                  username: comment.posterName
                })),
                statistics: {
                  views: details?.views,
                  averageViewDuration: details.averageViewDuration,
                  averageViewPercentage: details.averageViewPercentage,
                  likes: details?.likes,
                  dislikes: details.dislikes,
                  comments: details?.comments
                },
                info: {
                  avatar: details?.avatar,
                  name: details?.channelName,
                  followers: details?.subscribers
                }
              }
            : undefined
        }
      />

      <YoutubeAnalyticsRelatedPostsModal
        tag={selectedItem}
        postId={details?.id}
        onClose={resetSelectedItem}
        channelName={details?.channelName}
        channelId={selectedPost?.mainSocialAccountId}
      />
    </>
  );
};
